import * as Sentry from '@sentry/react'
import { Primitive } from '@sentry/types'

import { User } from '../types'

/**
 * Sends an error report to Datadog linked to an ErrorBoundary-handled crash.
 *
 * This function is used to add contextual information to the RUM session
 * in which the crash happens. It's not designed to be used outside of the
 * Sentry ErrorBoundary handling.
 *
 * @param {string} error The error and associated stacktrace to send to DD.
 */
function sendToDatadog(error: Error | null): void {
  const RumGlobal = (globalThis as GlobalThis).DD_RUM
  if (RumGlobal) {
    RumGlobal.addError?.(error, {
      scope: 'error-boundary',
    })
  }
}

/**
 * Raises a manual error report in Sentry.
 *
 * This function can be used to create manual error report outside of an
 * ErrorBoundary crash. e.g. you can raise a report within a handled try-catch
 * block to track the error, even if it is handled.
 *
 * @param {unknown} exception The Error/exception to send to Sentry
 * @param {User} user An object representing an authenticated user
 * @param {{ [key: string]: Primitive }} tags An object containing additional tags to be sent with the report
 */
function notify(exception: unknown, user?: User, tags?: { [key: string]: Primitive }): void {
  Sentry.withScope((scope) => {
    if (user) scope.setUser({ ip_address: '{{auto}}', ...user })
    if (tags) scope.setTags(tags)
    Sentry.captureException(exception)
  })
}

export { sendToDatadog, notify }
